import React, { useState, useRef, useCallback } from 'react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import ActivityModal from './ActivityModal';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import { fitMapToIslandAreaPoints, getCatalogMapViewport } from '../../lib/catalogMaps';

const LEAF_SRC = process.env.PUBLIC_URL + '/favicon-32x32.png';
const DISPLAY_SIZE = 30;

const mapContainerStyle = {
  width: '100%',
  height: '500px',
};

const mapOptions = {
  mapId: process.env.REACT_APP_MAP_ID,
  disableDefaultUI: true,
  zoomControl: true,
};

const LIBRARIES = ['places'];

export default function ActivitiesMapView({
  activities = [],
  onBoundsChanged,
  onViewChanged,
  initialCenter,
  initialZoom,
}) {
  const [selected, setSelected] = useState(null);
  const mapRef = useRef(null);
  const { island } = getRuntimeSiteContext();
  const islandViewport = getCatalogMapViewport(island);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const fallbackCenter = islandViewport.center;
  const fallbackZoom = islandViewport.zoom?.[isMobile ? 'mobile' : 'desktop'] ?? islandViewport.zoom?.desktop ?? 10;
  const hasSavedView =
    Number.isFinite(initialCenter?.lat) &&
    Number.isFinite(initialCenter?.lng) &&
    typeof initialZoom === 'number';

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
    mapIds: [process.env.REACT_APP_MAP_ID],
  });

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (!hasSavedView) {
      fitMapToIslandAreaPoints(map, island);
    }
  }, [hasSavedView, island]);

  const handleIdle = () => {
    if (!mapRef.current) return;

    if (onBoundsChanged) {
      const bounds = mapRef.current.getBounds();
      onBoundsChanged(bounds);
    }

    if (onViewChanged) {
      const center = mapRef.current.getCenter?.();
      const zoom = mapRef.current.getZoom?.();
      if (center && typeof zoom === 'number') {
        onViewChanged({
          center: { lat: center.lat(), lng: center.lng() },
          zoom,
        });
      }
    }
  };

  if (loadError) return <div>Error loading map</div>;
  if (!isLoaded) return <div>Loading map…</div>;

  return (
    <>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={hasSavedView ? initialCenter : fallbackCenter}
        zoom={hasSavedView ? initialZoom : fallbackZoom}
        options={mapOptions}
        onLoad={onMapLoad}
        onIdle={handleIdle}
      >
        {activities.map(activity => {
          const position = activity.position ?? activity.location;
          return (
            <Marker
              key={activity._id || activity.placeID}
              position={position}
              title={activity.name}
              icon={{
                url: LEAF_SRC,
                scaledSize: new window.google.maps.Size(DISPLAY_SIZE, DISPLAY_SIZE),
                anchor: new window.google.maps.Point(DISPLAY_SIZE / 2, DISPLAY_SIZE),
              }}
              onClick={() => setSelected(activity)}
            />
          );
        })}
      </GoogleMap>
      {selected && (
        <ActivityModal
          activity={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
